import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { CalendarIcon, Home, Users, Calendar as CalendarIconLucide, Plus, Search, Edit, Trash2, Building2, MapPin, Euro, BedDouble, Bath, Square } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import MapSelector from "./components/MapSelector";
import AgentProfile from "./components/AgentProfile";
import PropertyDetails from "./components/PropertyDetails";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [visits, setVisits] = useState([]);
  const [agents, setAgents] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");

  // Form states
  const [newProperty, setNewProperty] = useState({
    titre: "", description: "", type_propriete: "", type_transaction: "", prix: "", 
    surface: "", nombre_pieces: "", nombre_chambres: "", nombre_salles_bain: "", 
    adresse: "", ville: "", quartier: "", 
    latitude: "", longitude: "", images: []
  });
  
  const [editProperty, setEditProperty] = useState(null);
  const [isEditPropertyDialogOpen, setIsEditPropertyDialogOpen] = useState(false);
  
  const [editVisit, setEditVisit] = useState(null);
  const [isEditVisitDialogOpen, setIsEditVisitDialogOpen] = useState(false);

  const [editClient, setEditClient] = useState(null);
  const [isEditClientDialogOpen, setIsEditClientDialogOpen] = useState(false);
  
  const [editAgent, setEditAgent] = useState(null);
  const [isEditAgentDialogOpen, setIsEditAgentDialogOpen] = useState(false);
  
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentProfile, setShowAgentProfile] = useState(false);
  
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyDetails, setShowPropertyDetails] = useState(false);
  
  const [newClient, setNewClient] = useState({
    nom: "", prenom: "", email: "", telephone: "", adresse: "", ville: "",
    statut: "Prospect", budget_min: "", budget_max: "", type_recherche: "", commentaires: ""
  });

  const [newVisit, setNewVisit] = useState({
    property_id: "", client_id: "", agent_id: "", date_visite: null, 
    commentaires: "", notes_agent: "", date_rappel: null
  });

  const [newAgent, setNewAgent] = useState({
    nom: "", prenom: "", email: "", telephone: "", specialite: ""
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [isPropertyDialogOpen, setIsPropertyDialogOpen] = useState(false);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [isVisitDialogOpen, setIsVisitDialogOpen] = useState(false);
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);

  // Fetch data
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des propriétés");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des clients");
    }
  };

  const fetchVisits = async () => {
    try {
      const response = await axios.get(`${API}/visits`);
      setVisits(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des visites");
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des agents");
    }
  };

  useEffect(() => {
    fetchStats();
    fetchProperties();
    fetchClients();
    fetchVisits();
    fetchAgents();
  }, []);

  // Image upload function
  const uploadImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      console.log("Uploading file:", file.name);
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log("Upload response:", response.data);
      return response.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Erreur lors de l'upload: ${error.response?.data?.detail || error.message}`);
      return null;
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    toast.info(`Upload de ${files.length} image(s) en cours...`);
    const uploadedUrls = [];
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} n'est pas une image valide`);
        continue;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 5MB)`);
        continue;
      }
      
      const url = await uploadImage(file);
      if (url) {
        uploadedUrls.push(`${BACKEND_URL}${url}`);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setNewProperty(prev => ({
        ...prev, 
        images: [...prev.images, ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} image(s) ajoutée(s) avec succès`);
    }
    
    // Reset input
    event.target.value = '';
  };
  // Create functions
  const createProperty = async () => {
    try {
      // Validation des champs requis
      if (!newProperty.titre || !newProperty.description || !newProperty.type_propriete || 
          !newProperty.type_transaction || !newProperty.prix || !newProperty.surface ||
          !newProperty.adresse || !newProperty.ville) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      const propertyData = {
        ...newProperty,
        prix: parseFloat(newProperty.prix),
        surface: parseFloat(newProperty.surface),
        nombre_pieces: newProperty.nombre_pieces ? parseInt(newProperty.nombre_pieces) : null,
        nombre_chambres: newProperty.nombre_chambres ? parseInt(newProperty.nombre_chambres) : null,
        nombre_salles_bain: newProperty.nombre_salles_bain ? parseInt(newProperty.nombre_salles_bain) : null,
        latitude: newProperty.latitude ? parseFloat(newProperty.latitude) : null,
        longitude: newProperty.longitude ? parseFloat(newProperty.longitude) : null,
      };
      await axios.post(`${API}/properties`, propertyData);
      toast.success("Propriété ajoutée avec succès");
      setIsPropertyDialogOpen(false);
      setNewProperty({
        titre: "", description: "", type_propriete: "", type_transaction: "", prix: "", 
        surface: "", nombre_pieces: "", nombre_chambres: "", nombre_salles_bain: "", 
        adresse: "", ville: "", quartier: "", 
        latitude: "", longitude: "", images: []
      });
      fetchProperties();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la propriété");
    }
  };

  const createClient = async () => {
    try {
      // Validation des champs requis
      if (!newClient.nom || !newClient.prenom || !newClient.telephone) {
        toast.error("Veuillez remplir tous les champs obligatoires (nom, prénom, téléphone)");
        return;
      }

      const clientData = {
        ...newClient,
        budget_min: newClient.budget_min ? parseFloat(newClient.budget_min) : null,
        budget_max: newClient.budget_max ? parseFloat(newClient.budget_max) : null,
      };
      
      await axios.post(`${API}/clients`, clientData);
      toast.success("Client ajouté avec succès");
      setIsClientDialogOpen(false);
      setNewClient({
        nom: "", prenom: "", email: "", telephone: "", adresse: "", ville: "",
        statut: "Prospect", budget_min: "", budget_max: "", type_recherche: "", commentaires: ""
      });
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du client");
    }
  };

  const createVisit = async () => {
    try {
      // Validation des champs requis
      if (!newVisit.property_id || !newVisit.client_id || !newVisit.date_visite) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      const visitData = {
        ...newVisit,
        date_visite: newVisit.date_visite ? newVisit.date_visite.toISOString() : null,
      };
      
      await axios.post(`${API}/visits`, visitData);
      toast.success("Visite programmée avec succès");
      setIsVisitDialogOpen(false);
      setNewVisit({
        property_id: "", client_id: "", agent_id: "", date_visite: null, 
        commentaires: "", notes_agent: "", date_rappel: null
      });
      fetchVisits();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la programmation de la visite");
    }
  };

  const createAgent = async () => {
    try {
      // Validation des champs requis
      if (!newAgent.nom || !newAgent.prenom || !newAgent.email || !newAgent.telephone) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      await axios.post(`${API}/agents`, newAgent);
      toast.success("Agent ajouté avec succès");
      setIsAgentDialogOpen(false);
      setNewAgent({
        nom: "", prenom: "", email: "", telephone: "", specialite: ""
      });
      fetchAgents();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'agent");
    }
  };

  // Edit functions
  const editPropertyFn = (property) => {
    setEditProperty({
      ...property,
      prix: property.prix.toString(),
      surface: property.surface.toString(),
      nombre_pieces: property.nombre_pieces ? property.nombre_pieces.toString() : "",
      nombre_chambres: property.nombre_chambres ? property.nombre_chambres.toString() : "",
      nombre_salles_bain: property.nombre_salles_bain ? property.nombre_salles_bain.toString() : "",
      latitude: property.latitude ? property.latitude.toString() : "",
      longitude: property.longitude ? property.longitude.toString() : ""
    });
    setIsEditPropertyDialogOpen(true);
  };

  const updateProperty = async () => {
    try {
      if (!editProperty.titre || !editProperty.description || !editProperty.type_propriete || 
          !editProperty.type_transaction || !editProperty.prix || !editProperty.surface ||
          !editProperty.adresse || !editProperty.ville || !editProperty.code_postal) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      const propertyData = {
        ...editProperty,
        prix: parseFloat(editProperty.prix),
        surface: parseFloat(editProperty.surface),
        nombre_pieces: editProperty.nombre_pieces ? parseInt(editProperty.nombre_pieces) : null,
        nombre_chambres: editProperty.nombre_chambres ? parseInt(editProperty.nombre_chambres) : null,
        nombre_salles_bain: editProperty.nombre_salles_bain ? parseInt(editProperty.nombre_salles_bain) : null,
        latitude: editProperty.latitude ? parseFloat(editProperty.latitude) : null,
        longitude: editProperty.longitude ? parseFloat(editProperty.longitude) : null,
      };

      delete propertyData.id; // Remove ID from update data

      await axios.put(`${API}/properties/${editProperty.id}`, propertyData);
      toast.success("Propriété modifiée avec succès");
      setIsEditPropertyDialogOpen(false);
      setEditProperty(null);
      fetchProperties();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la modification de la propriété");
    }
  };

  const editVisitFn = (visit) => {
    setEditVisit({
      ...visit,
      date_visite: new Date(visit.date_visite),
      date_rappel: visit.date_rappel ? new Date(visit.date_rappel) : null
    });
    setIsEditVisitDialogOpen(true);
  };

  const updateVisit = async () => {
    try {
      if (!editVisit.property_id || !editVisit.client_id || !editVisit.date_visite) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      const visitData = {
        property_id: editVisit.property_id,
        client_id: editVisit.client_id,
        agent_id: editVisit.agent_id,
        statut: editVisit.statut,
        date_visite: editVisit.date_visite.toISOString(),
        date_rappel: editVisit.date_rappel ? editVisit.date_rappel.toISOString() : null,
        commentaires: editVisit.commentaires || "",
        notes_agent: editVisit.notes_agent || ""
      };

      console.log("Updating visit with data:", visitData);
      
      const response = await axios.put(`${API}/visits/${editVisit.id}`, visitData);
      console.log("Update response:", response.data);
      
      toast.success("Visite modifiée avec succès");
      setIsEditVisitDialogOpen(false);
      setEditVisit(null);
      
      // Force refresh data
      await Promise.all([fetchVisits(), fetchStats()]);
      
    } catch (error) {
      console.error("Update visit error:", error);
      toast.error(`Erreur lors de la modification: ${error.response?.data?.detail || error.message}`);
    }
  };

  const editClientFn = (client) => {
    setEditClient({
      ...client,
      budget_min: client.budget_min ? client.budget_min.toString() : "",
      budget_max: client.budget_max ? client.budget_max.toString() : ""
    });
    setIsEditClientDialogOpen(true);
  };

  const viewAgentProfile = (agent) => {
    console.log("viewAgentProfile called with agent:", agent);
    setSelectedAgent(agent);
    setShowAgentProfile(true);
    console.log("showAgentProfile set to true");
  };

  const closeAgentProfile = () => {
    setShowAgentProfile(false);
    setSelectedAgent(null);
  };

  const viewPropertyDetails = (property) => {
    setSelectedProperty(property);
    setShowPropertyDetails(true);
  };

  const closePropertyDetails = () => {
    setShowPropertyDetails(false);
    setSelectedProperty(null);
  };

  const updateClient = async () => {
    try {
      if (!editClient.nom || !editClient.prenom || !editClient.telephone) {
        toast.error("Veuillez remplir tous les champs obligatoires (nom, prénom, téléphone)");
        return;
      }

      const clientData = {
        ...editClient,
        budget_min: editClient.budget_min ? parseFloat(editClient.budget_min) : null,
        budget_max: editClient.budget_max ? parseFloat(editClient.budget_max) : null,
      };

      delete clientData.id; // Remove ID from update data

      await axios.put(`${API}/clients/${editClient.id}`, clientData);
      toast.success("Client modifié avec succès");
      setIsEditClientDialogOpen(false);
      setEditClient(null);
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la modification du client");
    }
  };

  // Filter functions
  const filteredProperties = properties.filter(property =>
    property.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.ville.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.type_propriete.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredClients = clients.filter(client =>
    client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (statut) => {
    switch (statut) {
      case "Disponible": return "bg-emerald-100 text-emerald-800";
      case "Vendu": return "bg-red-100 text-red-800";
      case "Loué": return "bg-blue-100 text-blue-800";
      case "Réservé": return "bg-orange-100 text-orange-800";
      case "Prospect": return "bg-yellow-100 text-yellow-800";
      case "Client": return "bg-green-100 text-green-800";
      case "Programmée": return "bg-blue-100 text-blue-800";
      case "Effectuée": return "bg-green-100 text-green-800";
      case "Annulée": return "bg-red-100 text-red-800";
      case "Reportée": return "bg-orange-100 text-orange-800";
      case "Rappel nécessaire": return "bg-purple-100 text-purple-800";
      case "En cours": return "bg-indigo-100 text-indigo-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-SN', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Desktop Header */}
      <div className="hidden md:block bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">AgenceImmoPro</h1>
                <p className="text-sm text-slate-600">Gestion complète de votre agence</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden mobile-header">
        <div className="mobile-header-content">
          <div className="mobile-logo">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-900">AgenceImmoPro</h1>
          </div>
          <Button variant="outline" size="sm" className="p-2">
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 md:px-6 py-3 md:py-6 mobile-main-content">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3 md:space-y-6">
          {/* Desktop Navigation */}
          <TabsList className="hidden md:grid w-full grid-cols-5 bg-white rounded-xl shadow-sm border">
            <TabsTrigger value="dashboard" className="flex items-center space-x-2">
              <Home className="h-4 w-4" />
              <span>Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="properties" className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>Biens</span>
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Clients</span>
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex items-center space-x-2">
              <CalendarIconLucide className="h-4 w-4" />
              <span>Visites</span>
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Agents</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-3 md:space-y-6">
            <div 
              className="relative overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 p-6 md:p-8 text-white shadow-xl"
              style={{
                backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.9), rgba(99, 102, 241, 0.9)), url('https://images.unsplash.com/photo-1497366754035-f200968a6e72')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="relative z-10">
                <h2 className="text-xl md:text-3xl font-bold mb-1 md:mb-2">Tableau de Bord</h2>
                <p className="text-blue-100 text-sm md:text-lg">Vue d'ensemble de votre activité</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              <Card className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600">Total Biens</p>
                      <p className="text-xl md:text-3xl font-bold text-slate-900">{stats.total_properties || 0}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-blue-50 rounded-lg md:rounded-xl">
                      <Building2 className="h-4 w-4 md:h-6 md:w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600">Clients</p>
                      <p className="text-xl md:text-3xl font-bold text-slate-900">{stats.total_clients || 0}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-green-50 rounded-lg md:rounded-xl">
                      <Users className="h-4 w-4 md:h-6 md:w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600">Visites</p>
                      <p className="text-xl md:text-3xl font-bold text-slate-900">{stats.upcoming_visits || 0}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-purple-50 rounded-lg md:rounded-xl">
                      <CalendarIconLucide className="h-4 w-4 md:h-6 md:w-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-slate-600">Prospects</p>
                      <p className="text-xl md:text-3xl font-bold text-slate-900">{stats.prospects || 0}</p>
                    </div>
                    <div className="p-2 md:p-3 bg-orange-50 rounded-lg md:rounded-xl">
                      <Users className="h-4 w-4 md:h-6 md:w-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                <CardHeader>
                  <CardTitle>Statut des Biens</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Disponibles</span>
                    <Badge className="bg-emerald-100 text-emerald-800">{stats.available_properties || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Vendus</span>
                    <Badge className="bg-red-100 text-red-800">{stats.sold_properties || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Loués</span>
                    <Badge className="bg-blue-100 text-blue-800">{stats.rented_properties || 0}</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                <CardHeader>
                  <CardTitle>Activité Récente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Visites effectuées</span>
                      <Badge className="bg-green-100 text-green-800">{stats.completed_visits || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Clients actifs</span>
                      <Badge className="bg-blue-100 text-blue-800">{stats.active_clients || 0}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties" className="space-y-6">
            {showPropertyDetails && selectedProperty ? (
              <PropertyDetails
                property={selectedProperty}
                onClose={closePropertyDetails}
              />
            ) : (
              <>
                <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-slate-900">Gestion des Biens</h3>
              <Dialog open={isPropertyDialogOpen} onOpenChange={setIsPropertyDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau Bien
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Ajouter un nouveau bien</DialogTitle>
                    <DialogDescription>
                      Remplissez les informations du bien immobilier
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="titre">Titre</Label>
                      <Input
                        id="titre"
                        value={newProperty.titre}
                        onChange={(e) => setNewProperty({...newProperty, titre: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="type_propriete">Type de propriété</Label>
                      <Select value={newProperty.type_propriete} onValueChange={(value) => setNewProperty({...newProperty, type_propriete: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Appartement">Appartement</SelectItem>
                          <SelectItem value="Maison">Maison</SelectItem>
                          <SelectItem value="Bureau">Bureau</SelectItem>
                          <SelectItem value="Commerce">Commerce</SelectItem>
                          <SelectItem value="Terrain">Terrain</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="type_transaction">Type de transaction</Label>
                      <Select value={newProperty.type_transaction} onValueChange={(value) => setNewProperty({...newProperty, type_transaction: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Vente">Vente</SelectItem>
                          <SelectItem value="Location">Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="prix">Prix (FCFA)</Label>
                      <Input
                        id="prix"
                        type="number"
                        value={newProperty.prix}
                        onChange={(e) => setNewProperty({...newProperty, prix: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="surface">Surface (m²)</Label>
                      <Input
                        id="surface"
                        type="number"
                        value={newProperty.surface}
                        onChange={(e) => setNewProperty({...newProperty, surface: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="nombre_pieces">Nombre de pièces</Label>
                      <Input
                        id="nombre_pieces"
                        type="number"
                        value={newProperty.nombre_pieces}
                        onChange={(e) => setNewProperty({...newProperty, nombre_pieces: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="nombre_chambres">Nombre de chambres</Label>
                      <Input
                        id="nombre_chambres"
                        type="number"
                        value={newProperty.nombre_chambres}
                        onChange={(e) => setNewProperty({...newProperty, nombre_chambres: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="nombre_salles_bain">Salles de bain</Label>
                      <Input
                        id="nombre_salles_bain"
                        type="number"
                        value={newProperty.nombre_salles_bain}
                        onChange={(e) => setNewProperty({...newProperty, nombre_salles_bain: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="adresse">Adresse</Label>
                      <Input
                        id="adresse"
                        value={newProperty.adresse}
                        onChange={(e) => setNewProperty({...newProperty, adresse: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ville">Ville</Label>
                      <Input
                        id="ville"
                        value={newProperty.ville}
                        onChange={(e) => setNewProperty({...newProperty, ville: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="quartier">Quartier (optionnel)</Label>
                      <Input
                        id="quartier"
                        value={newProperty.quartier}
                        onChange={(e) => setNewProperty({...newProperty, quartier: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Localisation GPS</Label>
                      <div className="border rounded-lg p-2">
                        <MapSelector
                          latitude={newProperty.latitude}
                          longitude={newProperty.longitude}
                          onLocationSelect={(location) => {
                            setNewProperty({
                              ...newProperty,
                              latitude: location.latitude.toString(),
                              longitude: location.longitude.toString()
                            });
                          }}
                          height="250px"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="images">Images du bien</Label>
                      <Input
                        id="images"
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="cursor-pointer"
                      />
                      {newProperty.images.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newProperty.images.map((img, index) => (
                            <div key={index} className="relative">
                              <img src={img} alt={`Image ${index + 1}`} className="h-16 w-16 object-cover rounded" />
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = newProperty.images.filter((_, i) => i !== index);
                                  setNewProperty({...newProperty, images: newImages});
                                }}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={newProperty.description}
                        onChange={(e) => setNewProperty({...newProperty, description: e.target.value})}
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createProperty}>Ajouter le bien</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {/* Edit Property Dialog */}
              <Dialog open={isEditPropertyDialogOpen} onOpenChange={setIsEditPropertyDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Modifier le bien</DialogTitle>
                    <DialogDescription>
                      Modifiez les informations du bien immobilier
                    </DialogDescription>
                  </DialogHeader>
                  {editProperty && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_titre">Titre</Label>
                        <Input
                          id="edit_titre"
                          value={editProperty.titre}
                          onChange={(e) => setEditProperty({...editProperty, titre: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_type_propriete">Type de propriété</Label>
                        <Select value={editProperty.type_propriete} onValueChange={(value) => setEditProperty({...editProperty, type_propriete: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Appartement">Appartement</SelectItem>
                            <SelectItem value="Maison">Maison</SelectItem>
                            <SelectItem value="Bureau">Bureau</SelectItem>
                            <SelectItem value="Commerce">Commerce</SelectItem>
                            <SelectItem value="Terrain">Terrain</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit_type_transaction">Type de transaction</Label>
                        <Select value={editProperty.type_transaction} onValueChange={(value) => setEditProperty({...editProperty, type_transaction: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Vente">Vente</SelectItem>
                            <SelectItem value="Location">Location</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit_prix">Prix (FCFA)</Label>
                        <Input
                          id="edit_prix"
                          type="number"
                          value={editProperty.prix}
                          onChange={(e) => setEditProperty({...editProperty, prix: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_surface">Surface (m²)</Label>
                        <Input
                          id="edit_surface"
                          type="number"
                          value={editProperty.surface}
                          onChange={(e) => setEditProperty({...editProperty, surface: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_nombre_pieces">Nombre de pièces</Label>
                        <Input
                          id="edit_nombre_pieces"
                          type="number"
                          value={editProperty.nombre_pieces}
                          onChange={(e) => setEditProperty({...editProperty, nombre_pieces: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_nombre_chambres">Nombre de chambres</Label>
                        <Input
                          id="edit_nombre_chambres"
                          type="number"
                          value={editProperty.nombre_chambres}
                          onChange={(e) => setEditProperty({...editProperty, nombre_chambres: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_nombre_salles_bain">Salles de bain</Label>
                        <Input
                          id="edit_nombre_salles_bain"
                          type="number"
                          value={editProperty.nombre_salles_bain}
                          onChange={(e) => setEditProperty({...editProperty, nombre_salles_bain: e.target.value})}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="edit_adresse">Adresse</Label>
                        <Input
                          id="edit_adresse"
                          value={editProperty.adresse}
                          onChange={(e) => setEditProperty({...editProperty, adresse: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_ville">Ville</Label>
                        <Input
                          id="edit_ville"
                          value={editProperty.ville}
                          onChange={(e) => setEditProperty({...editProperty, ville: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_code_postal">Code postal</Label>
                        <Input
                          id="edit_code_postal"
                          value={editProperty.code_postal}
                          onChange={(e) => setEditProperty({...editProperty, code_postal: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_quartier">Quartier</Label>
                        <Input
                          id="edit_quartier"
                          value={editProperty.quartier}
                          onChange={(e) => setEditProperty({...editProperty, quartier: e.target.value})}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Localisation GPS</Label>
                        <div className="border rounded-lg p-2">
                          <MapSelector
                            latitude={editProperty.latitude}
                            longitude={editProperty.longitude}
                            onLocationSelect={(location) => {
                              setEditProperty({
                                ...editProperty,
                                latitude: location.latitude.toString(),
                                longitude: location.longitude.toString()
                              });
                            }}
                            height="250px"
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="edit_description">Description</Label>
                        <Textarea
                          id="edit_description"
                          value={editProperty.description}
                          onChange={(e) => setEditProperty({...editProperty, description: e.target.value})}
                          rows={3}
                        />
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button onClick={updateProperty}>Enregistrer les modifications</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {filteredProperties.map((property) => (
                <Card key={property.id} className="bg-white shadow-sm hover:shadow-md transition-shadow border-0 ring-1 ring-slate-200 cursor-pointer" onClick={() => viewPropertyDetails(property)}>
                  <CardContent className="p-0">
                    <div 
                      className="h-40 md:h-48 bg-cover bg-center rounded-t-lg"
                      style={{
                        backgroundImage: property.images && property.images.length > 0 
                          ? `url('${property.images[0]}')`
                          : `url('https://images.unsplash.com/photo-1560518883-ce09059eeffa')`
                      }}
                    >
                      <div className="h-full bg-gradient-to-t from-black/60 to-transparent rounded-t-lg flex items-end p-3 md:p-4">
                        <Badge className={getStatusColor(property.statut)}>
                          {property.statut}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-4 md:p-6">
                      <div className="flex justify-between items-start mb-2 md:mb-3">
                        <h3 className="text-base md:text-lg font-semibold text-slate-900 line-clamp-2">{property.titre}</h3>
                        <Badge variant="outline" className="text-xs ml-2 flex-shrink-0">{property.type_propriete}</Badge>
                      </div>
                      
                      <div className="flex items-center text-slate-600 mb-2">
                        <MapPin className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                        <span className="text-sm">{property.ville}</span>
                      </div>
                      
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <div className="flex items-center text-emerald-600 font-bold text-sm md:text-base">
                          <Euro className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                          {formatPrice(property.prix)}
                        </div>
                        <div className="flex items-center text-slate-600">
                          <Square className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                          <span className="text-xs md:text-sm">{property.surface} m²</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-slate-600 text-xs md:text-sm mb-3 md:mb-4">
                        {property.nombre_pieces && (
                          <div className="flex items-center">
                            <Home className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            <span>{property.nombre_pieces} pcs</span>
                          </div>
                        )}
                        {property.nombre_chambres && (
                          <div className="flex items-center">
                            <BedDouble className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            <span>{property.nombre_chambres} ch</span>
                          </div>
                        )}
                        {property.nombre_salles_bain && (
                          <div className="flex items-center">
                            <Bath className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            <span>{property.nombre_salles_bain} sdb</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-slate-600 text-xs md:text-sm mb-3 md:mb-4 line-clamp-2">{property.description}</p>
                      
                      <div className="flex flex-col md:flex-row gap-2 md:justify-between">
                        <Button variant="outline" size="sm" onClick={(e) => {e.stopPropagation(); editPropertyFn(property);}} className="flex-1 md:flex-none">
                          <Edit className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                          <span className="hidden md:inline">Modifier</span>
                          <span className="md:hidden">Éditer</span>
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 flex-1 md:flex-none" onClick={(e) => e.stopPropagation()}>
                          <Trash2 className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                          <span className="hidden md:inline">Supprimer</span>
                          <span className="md:hidden">Suppr</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
              </>
            )}
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-slate-900">Gestion des Clients</h3>
              <Dialog open={isClientDialogOpen} onOpenChange={setIsClientDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau Client
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Ajouter un nouveau client</DialogTitle>
                    <DialogDescription>
                      Remplissez les informations du client
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom">Nom</Label>
                      <Input
                        id="nom"
                        value={newClient.nom}
                        onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="prenom">Prénom</Label>
                      <Input
                        id="prenom"
                        value={newClient.prenom}
                        onChange={(e) => setNewClient({...newClient, prenom: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone</Label>
                      <Input
                        id="telephone"
                        value={newClient.telephone}
                        onChange={(e) => setNewClient({...newClient, telephone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="statut_client">Statut</Label>
                      <Select value={newClient.statut} onValueChange={(value) => setNewClient({...newClient, statut: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Prospect">Prospect</SelectItem>
                          <SelectItem value="Client">Client</SelectItem>
                          <SelectItem value="Ancien Client">Ancien Client</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="type_recherche_client">Type de recherche</Label>
                      <Select value={newClient.type_recherche} onValueChange={(value) => setNewClient({...newClient, type_recherche: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Vente">Vente</SelectItem>
                          <SelectItem value="Location">Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="budget_min">Budget minimum (FCFA)</Label>
                      <Input
                        id="budget_min"
                        type="number"
                        value={newClient.budget_min}
                        onChange={(e) => setNewClient({...newClient, budget_min: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="budget_max">Budget maximum (FCFA)</Label>
                      <Input
                        id="budget_max"
                        type="number"
                        value={newClient.budget_max}
                        onChange={(e) => setNewClient({...newClient, budget_max: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="adresse_client">Adresse</Label>
                      <Input
                        id="adresse_client"
                        value={newClient.adresse}
                        onChange={(e) => setNewClient({...newClient, adresse: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ville_client">Ville</Label>
                      <Input
                        id="ville_client"
                        value={newClient.ville}
                        onChange={(e) => setNewClient({...newClient, ville: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="commentaires_client">Commentaires</Label>
                      <Textarea
                        id="commentaires_client"
                        value={newClient.commentaires}
                        onChange={(e) => setNewClient({...newClient, commentaires: e.target.value})}
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createClient}>Ajouter le client</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Edit Client Dialog */}
              <Dialog open={isEditClientDialogOpen} onOpenChange={setIsEditClientDialogOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Modifier le client</DialogTitle>
                    <DialogDescription>
                      Modifiez les informations du client
                    </DialogDescription>
                  </DialogHeader>
                  {editClient && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_nom">Nom *</Label>
                        <Input
                          id="edit_nom"
                          value={editClient.nom}
                          onChange={(e) => setEditClient({...editClient, nom: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_prenom">Prénom *</Label>
                        <Input
                          id="edit_prenom"
                          value={editClient.prenom}
                          onChange={(e) => setEditClient({...editClient, prenom: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_email">Email</Label>
                        <Input
                          id="edit_email"
                          type="email"
                          value={editClient.email || ""}
                          onChange={(e) => setEditClient({...editClient, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_telephone">Téléphone *</Label>
                        <Input
                          id="edit_telephone"
                          value={editClient.telephone}
                          onChange={(e) => setEditClient({...editClient, telephone: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_statut_client">Statut</Label>
                        <Select value={editClient.statut} onValueChange={(value) => setEditClient({...editClient, statut: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Prospect">Prospect</SelectItem>
                            <SelectItem value="Client">Client</SelectItem>
                            <SelectItem value="Ancien Client">Ancien Client</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit_type_recherche_client">Type de recherche</Label>
                        <Select value={editClient.type_recherche || ""} onValueChange={(value) => setEditClient({...editClient, type_recherche: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Vente">Vente</SelectItem>
                            <SelectItem value="Location">Location</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit_budget_min">Budget minimum (FCFA)</Label>
                        <Input
                          id="edit_budget_min"
                          type="number"
                          value={editClient.budget_min}
                          onChange={(e) => setEditClient({...editClient, budget_min: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_budget_max">Budget maximum (FCFA)</Label>
                        <Input
                          id="edit_budget_max"
                          type="number"
                          value={editClient.budget_max}
                          onChange={(e) => setEditClient({...editClient, budget_max: e.target.value})}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="edit_adresse_client">Adresse</Label>
                        <Input
                          id="edit_adresse_client"
                          value={editClient.adresse || ""}
                          onChange={(e) => setEditClient({...editClient, adresse: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_ville_client">Ville</Label>
                        <Input
                          id="edit_ville_client"
                          value={editClient.ville || ""}
                          onChange={(e) => setEditClient({...editClient, ville: e.target.value})}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="edit_commentaires_client">Commentaires</Label>
                        <Textarea
                          id="edit_commentaires_client"
                          value={editClient.commentaires || ""}
                          onChange={(e) => setEditClient({...editClient, commentaires: e.target.value})}
                          rows={3}
                        />
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button onClick={updateClient}>Enregistrer les modifications</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map((client) => (
                <Card key={client.id} className="bg-white shadow-sm hover:shadow-md transition-shadow border-0 ring-1 ring-slate-200">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {client.prenom} {client.nom}
                        </h3>
                        <p className="text-slate-600 text-sm">{client.email}</p>
                      </div>
                      <Badge className={getStatusColor(client.statut)}>
                        {client.statut}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm text-slate-600">
                      <p><strong>Téléphone:</strong> {client.telephone}</p>
                      {client.ville && <p><strong>Ville:</strong> {client.ville}</p>}
                      {client.type_recherche && (
                        <p><strong>Recherche:</strong> {client.type_recherche}</p>
                      )}
                      {client.budget_min && client.budget_max && (
                        <p><strong>Budget:</strong> {formatPrice(client.budget_min)} - {formatPrice(client.budget_max)}</p>
                      )}
                    </div>
                    
                    <div className="flex justify-between mt-4">
                      <Button variant="outline" size="sm" onClick={() => editClientFn(client)}>
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-slate-900">Gestion des Visites</h3>
              <Dialog open={isVisitDialogOpen} onOpenChange={setIsVisitDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvelle Visite
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Programmer une nouvelle visite</DialogTitle>
                    <DialogDescription>
                      Sélectionnez la propriété, le client et la date de visite
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="property_select">Propriété</Label>
                      <Select value={newVisit.property_id} onValueChange={(value) => setNewVisit({...newVisit, property_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez une propriété" />
                        </SelectTrigger>
                        <SelectContent>
                          {properties.map((property) => (
                            <SelectItem key={property.id} value={property.id}>
                              {property.titre} - {property.ville}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="client_select">Client</Label>
                      <Select value={newVisit.client_id} onValueChange={(value) => setNewVisit({...newVisit, client_id: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez un client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.prenom} {client.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date de visite</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newVisit.date_visite ? format(newVisit.date_visite, "PPP", { locale: fr }) : "Sélectionner une date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newVisit.date_visite}
                            onSelect={(date) => setNewVisit({...newVisit, date_visite: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label htmlFor="commentaires_visite">Commentaires</Label>
                      <Textarea
                        id="commentaires_visite"
                        value={newVisit.commentaires}
                        onChange={(e) => setNewVisit({...newVisit, commentaires: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes_agent">Notes agent</Label>
                      <Textarea
                        id="notes_agent"
                        value={newVisit.notes_agent}
                        onChange={(e) => setNewVisit({...newVisit, notes_agent: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Date de rappel (optionnel)</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newVisit.date_rappel ? format(newVisit.date_rappel, "PPP", { locale: fr }) : "Sélectionner une date de rappel"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newVisit.date_rappel}
                            onSelect={(date) => setNewVisit({...newVisit, date_rappel: date})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createVisit}>Programmer la visite</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Edit Visit Dialog */}
              <Dialog open={isEditVisitDialogOpen} onOpenChange={setIsEditVisitDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Modifier la visite</DialogTitle>
                    <DialogDescription>
                      Modifiez les informations de la visite
                    </DialogDescription>
                  </DialogHeader>
                  {editVisit && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit_property_select">Propriété</Label>
                        <Select value={editVisit.property_id} onValueChange={(value) => setEditVisit({...editVisit, property_id: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez une propriété" />
                          </SelectTrigger>
                          <SelectContent>
                            {properties.map((property) => (
                              <SelectItem key={property.id} value={property.id}>
                                {property.titre} - {property.ville}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit_client_select">Client</Label>
                        <Select value={editVisit.client_id} onValueChange={(value) => setEditVisit({...editVisit, client_id: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez un client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.prenom} {client.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit_statut_visite">Statut de la visite</Label>
                        <Select value={editVisit.statut} onValueChange={(value) => setEditVisit({...editVisit, statut: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez un statut" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Programmée">Programmée</SelectItem>
                            <SelectItem value="En cours">En cours</SelectItem>
                            <SelectItem value="Effectuée">Effectuée</SelectItem>
                            <SelectItem value="Reportée">Reportée</SelectItem>
                            <SelectItem value="Annulée">Annulée</SelectItem>
                            <SelectItem value="Rappel nécessaire">Rappel nécessaire</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Date de visite</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editVisit.date_visite ? format(editVisit.date_visite, "PPP", { locale: fr }) : "Sélectionner une date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={editVisit.date_visite}
                              onSelect={(date) => setEditVisit({...editVisit, date_visite: date})}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label>Date de rappel (optionnel)</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editVisit.date_rappel ? format(editVisit.date_rappel, "PPP", { locale: fr }) : "Sélectionner une date de rappel"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={editVisit.date_rappel}
                              onSelect={(date) => setEditVisit({...editVisit, date_rappel: date})}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label htmlFor="edit_commentaires_visite">Commentaires</Label>
                        <Textarea
                          id="edit_commentaires_visite"
                          value={editVisit.commentaires}
                          onChange={(e) => setEditVisit({...editVisit, commentaires: e.target.value})}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_notes_agent">Notes agent</Label>
                        <Textarea
                          id="edit_notes_agent"
                          value={editVisit.notes_agent}
                          onChange={(e) => setEditVisit({...editVisit, notes_agent: e.target.value})}
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button onClick={updateVisit}>Enregistrer les modifications</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {visits.map((visit) => {
                const property = properties.find(p => p.id === visit.property_id);
                const client = clients.find(c => c.id === visit.client_id);
                
                return (
                  <Card key={visit.id} className="bg-white shadow-sm border-0 ring-1 ring-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">
                                {property ? property.titre : "Propriété inconnue"}
                              </h3>
                              <p className="text-slate-600">
                                Client: {client ? `${client.prenom} ${client.nom}` : "Client inconnu"}
                              </p>
                            </div>
                            <Badge className={getStatusColor(visit.statut)}>
                              {visit.statut}
                            </Badge>
                          </div>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-slate-600">
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {format(new Date(visit.date_visite), "PPP", { locale: fr })}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {property ? property.ville : "Ville inconnue"}
                            </div>
                          </div>
                          {visit.commentaires && (
                            <p className="text-slate-600 text-sm mt-2">{visit.commentaires}</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => editVisitFn(visit)}>
                            <Edit className="h-4 w-4 mr-1" />
                            Modifier
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Agents Tab */}
          <TabsContent value="agents" className="space-y-6">
            {showAgentProfile && selectedAgent ? (
              <AgentProfile
                agent={selectedAgent}
                onClose={closeAgentProfile}
                BACKEND_URL={BACKEND_URL}
                API={API}
              />
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-bold text-slate-900">Gestion des Agents</h3>
              <Dialog open={isAgentDialogOpen} onOpenChange={setIsAgentDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvel Agent
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter un nouvel agent</DialogTitle>
                    <DialogDescription>
                      Remplissez les informations de l'agent immobilier
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nom_agent">Nom</Label>
                        <Input
                          id="nom_agent"
                          value={newAgent.nom}
                          onChange={(e) => setNewAgent({...newAgent, nom: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="prenom_agent">Prénom</Label>
                        <Input
                          id="prenom_agent"
                          value={newAgent.prenom}
                          onChange={(e) => setNewAgent({...newAgent, prenom: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email_agent">Email</Label>
                      <Input
                        id="email_agent"
                        type="email"
                        value={newAgent.email}
                        onChange={(e) => setNewAgent({...newAgent, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="telephone_agent">Téléphone</Label>
                      <Input
                        id="telephone_agent"
                        value={newAgent.telephone}
                        onChange={(e) => setNewAgent({...newAgent, telephone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="specialite">Spécialité</Label>
                      <Input
                        id="specialite"
                        value={newAgent.specialite}
                        onChange={(e) => setNewAgent({...newAgent, specialite: e.target.value})}
                        placeholder="Ex: Résidentiel, Commercial, Luxe..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createAgent}>Ajouter l'agent</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agents.map((agent) => (
                <Card key={agent.id} className="bg-white shadow-sm hover:shadow-md transition-shadow border-0 ring-1 ring-slate-200">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {agent.prenom[0]}{agent.nom[0]}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {agent.prenom} {agent.nom}
                        </h3>
                        <p className="text-slate-600 text-sm">{agent.email}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm text-slate-600">
                      <p><strong>Téléphone:</strong> {agent.telephone}</p>
                      {agent.specialite && (
                        <p><strong>Spécialité:</strong> {agent.specialite}</p>
                      )}
                      <p><strong>Membre depuis:</strong> {format(new Date(agent.date_creation), "PPP", { locale: fr })}</p>
                    </div>
                    
                    <div className="flex justify-between mt-4">
                      <Button variant="outline" size="sm" onClick={() => viewAgentProfile(agent)}>
                        <Users className="h-4 w-4 mr-1" />
                        Voir Profil
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden mobile-nav">
        <div className="mobile-nav-grid">
          <button 
            className={`mobile-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <Home className="mobile-nav-icon" />
            <span>Accueil</span>
          </button>
          <button 
            className={`mobile-nav-item ${activeTab === 'properties' ? 'active' : ''}`}
            onClick={() => setActiveTab('properties')}
          >
            <Building2 className="mobile-nav-icon" />
            <span>Biens</span>
          </button>
          <button 
            className={`mobile-nav-item ${activeTab === 'clients' ? 'active' : ''}`}
            onClick={() => setActiveTab('clients')}
          >
            <Users className="mobile-nav-icon" />
            <span>Clients</span>
          </button>
          <button 
            className={`mobile-nav-item ${activeTab === 'visits' ? 'active' : ''}`}
            onClick={() => setActiveTab('visits')}
          >
            <CalendarIconLucide className="mobile-nav-icon" />
            <span>Visites</span>
          </button>
          <button 
            className={`mobile-nav-item ${activeTab === 'agents' ? 'active' : ''}`}
            onClick={() => setActiveTab('agents')}
          >
            <Users className="mobile-nav-icon" />
            <span>Agents</span>
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;